/* Copyright 2020-2021 The MathWorks, Inc. */
define(['mw-widget-api/WidgetBase',
    'mw-widget-api/defineWidget',
    'mw-widget-api/facade/html',
    'dojo/i18n!mw-rating-feedback/nls/main',
    'mw-log/Log',
    'mw-rating-feedback/impl/RatingFeedbackStep',
    'mw-rating/Rating'
], function (WidgetBase, defineWidget, html, l10n, log) {
    'use strict';

    const RATING_FEEDBACK_NAME_PREFIX = 'mw_rating_feedback_';
    const RATING_FEEDBACK_CLASS_PREFIX = 'mwRatingFeedback-';
    const VALUE_WIDGET_CLASS_IDENTIFIER = `${RATING_FEEDBACK_CLASS_PREFIX}Value`;

    const HIDDEN_CLASS = `${RATING_FEEDBACK_CLASS_PREFIX}Hidden`;

    const NAME_SEPARATOR = '__';

    const MAX_STEPS = 3;

    const STEP_RATING = 0;
    const STEP_COMMENT = 1;
    const STEP_COMPLETE = 2;

    let idCounter = 0;
    function generateId () {
        // Return a string that will be unique.
        idCounter += 1;
        return `${RATING_FEEDBACK_NAME_PREFIX}${idCounter}`;
    }

    const STEP_TEMPLATES = [];
    const BUTTON_TEMPLATE = (feedbackWidget, isVisible) => {
        return html`<button .disabled=${!isVisible} class="${RATING_FEEDBACK_CLASS_PREFIX}NextButton" aria-label="${feedbackWidget.forwardLabelAria || l10n.forwardLabelAria}" @click=${feedbackWidget._handleNext}>${feedbackWidget.forwardLabel || l10n.forwardLabel}</button>`;
    };

    // We keep all the steps in the DOM so we can use
    // CSS transitions to go between them.
    // But this means we cannot set them to display: none,
    // since this is not animatable.  It also means we can't
    // use visible: hidden - since we want it to fade out
    // we could use animation listeners to address those items
    // but that complicates the widget logic more.
    // Instead we hide the content from screen readers using
    // aria-hidden="true" - since the user can't see it either
    // and set the widgets to disabled to avoid "focus" problems
    STEP_TEMPLATES[STEP_RATING] = (feedbackWidget) => {
        let id = generateId();
        let isVisible = feedbackWidget.step === STEP_RATING;

        // Use of @keyup for mw-rating is intentional for handing Commit key.
        // If keydown is used comment area see the keyup and inserts a new line.
        return html`
            <div 
                class="${isVisible ? '' : HIDDEN_CLASS}"
                aria-hidden="${!isVisible}"
            >
                <label class="${RATING_FEEDBACK_CLASS_PREFIX}Label" id="${id}">${feedbackWidget.steps[STEP_RATING]}</label>
                <mw-rating 
                    .doesDisplayRating=${false} 
                    aria-labelledby="${id}"
                    .disabled=${!isVisible}  
                    class="${VALUE_WIDGET_CLASS_IDENTIFIER}" 
                    name="${id}${NAME_SEPARATOR}rating" 
                    @change=${feedbackWidget._handleNext} 
                    @keyup=${feedbackWidget._handleKeyCommitsForRating}>
                </mw-rating>
            </div>`;
    };
    STEP_TEMPLATES[STEP_COMMENT] = (feedbackWidget) => {
        let id = generateId();
        let isVisible = feedbackWidget.step === STEP_COMMENT;

        return html`
            <div 
                class="${isVisible ? '' : HIDDEN_CLASS}"
                aria-hidden="${!isVisible}"
            >
                <label class="${RATING_FEEDBACK_CLASS_PREFIX}Label" for="${id}">${feedbackWidget.steps[STEP_COMMENT]}</label>
                <textarea .disabled=${!isVisible} id="${id}" class="${VALUE_WIDGET_CLASS_IDENTIFIER}" name="${id}${NAME_SEPARATOR}comment" spellcheck></textarea>
                ${BUTTON_TEMPLATE(feedbackWidget, isVisible)}
            </div>`;
    };
    STEP_TEMPLATES[STEP_COMPLETE] = (feedbackWidget) => {
        let isVisible = feedbackWidget.step === STEP_COMPLETE;

        // Using hidden so NVDA announces last step
        // this prevents the animation because you cannot
        // animate 'hidden'.  Adding an animation would be difficult
        // without some directives (or writing a state machine).
        // to "delay the draw" of some of the classes or the
        // step content 1 frame.
        // Therefore, turning off the animation on the last step
        // to be more accessible.
        return html`
            <div 
                role="alert"
                class="${isVisible ? '' : HIDDEN_CLASS}"
                aria-hidden="${!isVisible}"
                .hidden=${!isVisible}
            >
                <div>${feedbackWidget.steps[STEP_COMPLETE]}</div>
            </div>`;
    };

    class RatingFeedback extends WidgetBase {
        static get properties () {
            return {
                step: {
                    reflect: true,
                    type: Number
                },
                forwardLabel: {
                    reflect: true,
                    type: String
                },
                forwardLabelAria: {
                    reflect: true,
                    type: String
                },
                steps: {
                    reflect: false,
                    type: Array
                },
                dataTestId: {
                    reflect: true,
                    type: String,
                    attribute: 'data-test-id'
                }
            };
        }

        /**
         * Creates a mw-rating-feedback widget
         * @param options {Object} - Initial Object State
         * @param options.step {Number} - The step to start displaying first - default is 0.
         * @param options.steps {Array} - An array of steps to display.  Each item should be
         * a DOM Node or Document Fragment.  Can also be specified via <mw-rating-step step="0"> children
         * @param options.forwardLabel {String} Get/Set the visible label on the button to go to the next step
         * the default is in the nls package as "forwardLabel" and is set to ">"
         * @param options.forwardLabelAria {String} Get/Set the aria label on the button to go to the next step
         * the default is in the nls package as "forwardLabelAria" and is set to "Next"
         * @param options.dataTestId {String} A user specified id used for testing, reflects to 'data-test-id'
         */
        constructor () {
            super();
            this.step = 0;
            this.forwardLabel = l10n.forwardLabel;
            this.forwardLabelAria = l10n.forwardLabelAria;
            this._steps = [];
            this._currentValues = { };
            this._updateValues(this._currentValues, { emit: false });
        }

        /**
         * Get/Set the visible label on the button to go to the next step
         * the default is in the nls package as "forwardLabel" and
         * is set to ">"
         * @member forwardLabel {String}
         * @memberof RatingFeedback
         * @instance
         */

        /**
         * Get/Set the aria label on the button to go to the next step
         * the default is in the nls package as "forwardLabelAria" and
         * is set to "Next"
         * @member forwardLabelAria {String}
         * @memberof RatingFeedback
         * @instance
         */

        /**
         * A user specified id used for testing, reflects to 'data-test-id'
         * @member dataTestId {String}
         * @memberof RatingFeedback
         * @instance
         */

        connectedCallback () {
            this.steps = this._processDomSteps(this._getStepsFromDom());
            super.connectedCallback();
        }

        _getStepsFromDom () {
            let rawSteps = Array.from(this.querySelectorAll('mw-rating-feedback-step'));
            return rawSteps;
        }

        _getWidgetValues () {
            let values = Array.from(this.querySelectorAll(`.${VALUE_WIDGET_CLASS_IDENTIFIER}`));
            let results = {};
            values.forEach((node) => {
                if (node.name) {
                    let name = node.name;
                    if (name.indexOf(NAME_SEPARATOR) >= 0) {
                        name = name.split(NAME_SEPARATOR)[1];
                    }
                    results[name] = node.value;
                }
            });
            return results;
        }

        _parseFeedbackStep (feedbackStep) {
            // Convert step to numeric
            let integerStep = Math.floor(feedbackStep.step);
            if (isNaN(integerStep)) {
                log.warn(`Could not parse step value: ${feedbackStep.step}`);
                return null;
            } else if (integerStep >= MAX_STEPS) {
                log.warn(`Max step number (${MAX_STEPS - 1}) exceeded: ${feedbackStep.step}`);
                return null;
            } else if (integerStep < 0) {
                log.warn(`Negative step numbers are not allowed: ${feedbackStep.step}`);
                return null;
            }
            feedbackStep.step = integerStep;
            return feedbackStep;
        }
        _extractDomContentToDocFragment (domNode) {
            let range = document.createRange();
            range.selectNodeContents(domNode);
            let docFragment = range.extractContents();
            return docFragment;
        }

        _processDomSteps (rawSteps) {
            let processedSteps = [
                this._steps[0] || document.createDocumentFragment(),
                this._steps[1] || document.createDocumentFragment(),
                this._steps[2] || document.createDocumentFragment()
            ];
            rawSteps.forEach((feedbackStep) => {
                let parsedFeedback = this._parseFeedbackStep(feedbackStep);
                let stepContents = this._extractDomContentToDocFragment(parsedFeedback);
                processedSteps[parsedFeedback.step] = stepContents;
            });

            return processedSteps;
        }

        /**
         * Set or Get the current step number.
         * @type {Integer}
         */
        get step () {
            return this._step;
        }

        set step (value) {
            let oldVal = this._step;
            this._step = Math.min(Math.max(value, STEP_RATING), STEP_COMPLETE);
            this.requestUpdate('step', oldVal);
        }

        /**
         * Set or Get an array of steps. Each array items should be a
         * document fragment or node.  The item is content displayed
         * for each step of the process.
         *
         * step[0] = Rating
         * step[1] = Commment
         * step[2] = Complete
         *
         * No more than 3 steps should be provided.
         * @type {Array}
         */
        get steps () {
            // Copy array, so we don't ever expose our internal array
            // push and pop would break this widget.
            // instead set the whole steps array when modifying
            return [...this._steps];
        }

        set steps (rawSteps) {
            let oldVal = this._steps;
            if (rawSteps.length !== MAX_STEPS) {
                log.warn(`${MAX_STEPS} steps must be provided to the widget, number provided: ${rawSteps.length}`);
            }
            for (let i = 0; i < MAX_STEPS; i += 1) {
                if (rawSteps[i]) {
                    if (!(rawSteps[i] instanceof window.Node)) {
                        log.warn(`Step ${i} is not a DOM node, ignoring`);
                        this._steps[i] = document.createDocumentFragment();
                    } else {
                        this._steps[i] = rawSteps[i];
                    }
                }
            }
            this.requestUpdate('steps', oldVal);
        }

        /**
         *
         * @returns {Object} currentValues The currently set values of the widget
         * @returns {Boolean} currentValues.complete The user completed the entire workflow (all steps)
         * @returns {Number} currentValues.rating The rating the user selected
         * @returns {String} currentValues.comment The comment the user entered
         */
        getValues () {
            return this._currentValues;
        }

        _updateValues (values, options = { emit: true }) {
            Object.assign(this._currentValues, values);
            this._currentValues.complete = false;
            if (this.step === STEP_COMPLETE) {
                this._currentValues.complete = true;
            }
            if (options.emit) {
                this.emit('ratingstepchange', {
                    values: this._currentValues,
                    step: this.step - 1
                });
                if (this._currentValues.complete) {
                    this.emit('ratingcomplete', {
                        values: this._currentValues
                    });
                }
            }
        }

        _handleKeyCommitsForRating (evt) {
            if (evt.isComposing || evt.keyCode === 229) {
                // Ignore any IME input
                return;
            }
            if (evt.key === 'Enter') {
                // Force commit by leaving widget, unless no stars selected
                // g2451317
                if (evt.currentTarget.value > 0) {
                    evt.target.blur();
                }
            }
        }

        _handleNext (evt) {
            this._moveToNextStep();
        }

        _moveToNextStep () {
            let values = this._getWidgetValues();
            this.step += 1;
            this._updateValues(values);
        }

        updated (changedProperties) {
            if (changedProperties.has('step')) {
                // Set Focus to new widget if step just changed.
                if (this.step !== changedProperties.get('step')) {
                    let widget = this.querySelector(`.${VALUE_WIDGET_CLASS_IDENTIFIER}`);
                    if (widget) {
                        widget.focus();
                    }
                }
            }
        }

        render () {
            let litTemplates = STEP_TEMPLATES
                .map((template) =>
                    template(this)
                );

            // Reverse the order, we want the newer template on top
            // as we move through, since the newer template is growing
            // during animation, and the older template is shrinking
            // The older template will appear in the view shrinking
            // newer template will expand above the older template
            // taking the place of the older template as it shrinks to
            // nothing
            return html`${litTemplates.reverse()}`;
        }
    }
    return defineWidget({
        name: 'mw-rating-feedback',
        widgetClass: RatingFeedback
    });
});
